import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { IftaFiling, MILES_SOURCES, UploadResponse } from 'src/models';
import Iconify from 'src/components/iconify';
//
import FileDrop from './file-drop';
import MilesInstructions from './miles-instructions';
import SkipCard from './skip-card';
import UploadedFilesList from './uploaded-files-list';
import ColumnMappingCard from './column-mapping-card';
import { MilesPreviewTable } from './parsed-preview-table';

import StepTitle from './step-title';

// ----------------------------------------------------------------------

type Props = {
  onBack?: () => void;
  onSkip: () => void;
  filing: IftaFiling;
  canImportCt: boolean;
  onUploaded: (filing: IftaFiling) => void;
  onContinue: () => void;
};

export default function StepMilesUpload({ filing, canImportCt, onUploaded, onContinue, onBack, onSkip }: Props) {
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [imported, setImported] = useState(false);

  const showErrors = (error: any) => {
    const messages: BackendValidationError[] = error?.response?.data;
    if (Array.isArray(messages)) {
      messages.forEach((message) => enqueueSnackbar(message.message, { variant: 'error' }));
    } else {
      enqueueSnackbar('Something went wrong uploading your file', { variant: 'error' });
    }
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post<UploadResponse>(
        formatEndpoint(endpoints.filings.uploadMiles, { id: filing.id }),
        formData
      );
      setResult(response.data);
      setImported(false);
      onUploaded(response.data.filing);
    } catch (error) {
      showErrors(error);
    } finally {
      setUploading(false);
    }
  };

  const importFromCt = async () => {
    setImporting(true);
    try {
      const response = await axios.post(
        formatEndpoint(endpoints.filings.autoImportMiles, { id: filing.id })
      );
      setResult(null);
      setImported(true);
      onUploaded(response.data.filing);
    } catch (error) {
      showErrors(error);
    } finally {
      setImporting(false);
    }
  };

  const isCtSource = filing.milesSource === 'counting_trucks';
  const previewLines = filing.mileLines;
  const hasData = previewLines.length > 0;

  return (
    <Stack spacing={3}>
      <StepTitle title="Your mileage data" onBack={onBack} />

      <MilesInstructions source={filing.milesSource} quarter={filing.selectedQuarter} />

      {isCtSource && canImportCt && (
        <Card variant="outlined" sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Iconify icon="solar:map-bold" width={32} sx={{ color: 'primary.dark' }} />
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1">Import from Counting Trucks</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                One click pulls per-state miles for {filing.selectedQuarter} straight from your GPS data.
              </Typography>
            </Stack>
            <LoadingButton
              variant="contained"
              color="secondary"
              loading={importing}
              onClick={importFromCt}
              data-testid="ct-import-button"
            >
              Import miles
            </LoadingButton>
          </Stack>
        </Card>
      )}

      {isCtSource && canImportCt && <Divider>or upload a report instead</Divider>}

      <UploadedFilesList
        filingId={filing.id}
        files={filing.mileFiles}
        lineCountFor={(fileName) => filing.mileLines.filter((line) => line.importedFromFile === fileName).length}
        removeEndpoint={endpoints.filings.removeMilesFile}
        onRemoved={onUploaded}
      />

      <FileDrop
        uploading={uploading}
        onFile={uploadFile}
        hint={
          filing.mileFiles.length > 0
            ? 'Add another mileage report, from any provider (CSV or XLSX)'
            : "Your provider's IFTA mileage report (CSV or XLSX)"
        }
      />

      {result?.mappingSource === 'ai' && (
        <ColumnMappingCard mapping={result.mapping} headers={result.headers} kind="miles" />
      )}

      {result && result.unknownJurisdictions.length > 0 && (
        <Typography variant="body2" sx={{ color: 'warning.dark' }}>
          Skipped non-IFTA jurisdictions: {result.unknownJurisdictions.join(', ')}
        </Typography>
      )}

      {hasData && (
        <Card variant="outlined">
          <MilesPreviewTable lines={previewLines} />
        </Card>
      )}

      {hasData && (
        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={onContinue}
          sx={{ alignSelf: 'flex-start' }}
          data-testid="miles-continue"
        >
          Looks good, continue
        </Button>
      )}

      {!hasData && (
        <SkipCard
          title="Don't have this report yet?"
          body={`If you can't get into ${
            MILES_SOURCES.find((option) => option.value === filing.milesSource)?.label || 'your provider'
          } right now, or you're still waiting on the file, skip ahead and add it later. We can't calculate your filing until it's uploaded.`}
          onSkip={onSkip}
        />
      )}
    </Stack>
  );
}
